import { mix, parseColour } from '@/lib/public/theme-colours';
import type { BackgroundEffect } from '@/lib/public/effects/catalogue';

/**
 * How strongly each effect draws, worked out per page so it reads as the
 * same quiet step on a white page, a navy one or a black one.
 *
 * Every effect is drawn at full strength inside its own layer, and the layer
 * gets one opacity: the one at which its ink over the page lands a set
 * number of L* steps from the page. Thin lines need a bigger step than the
 * logo watermark (a filled shape) to read at all; none of them get near the
 * contrast of text, so headings, buttons and ticket rows stay on top.
 */

function channelToLinear(c: number): number {
  const s = c / 255;
  return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
}

/** CIE L* (0 to 100), the perceptual lightness of a colour. */
export function lightness(colour: string): number | null {
  const rgb = parseColour(colour);
  if (!rgb) return null;
  const [r, g, b] = rgb.map(channelToLinear);
  const y = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return y <= 216 / 24389 ? (24389 / 27) * y : 116 * Math.cbrt(y) - 16;
}

/**
 * The opacity at which `ink` over `page` lands `step` L* away from the page,
 * clamped to [min, max]. Returns 0 when the ink can't be told apart from the
 * page at all (nothing worth drawing).
 */
export function solveOpacity(page: string, ink: string, step: number, min: number, max: number): number {
  const lPage = lightness(page);
  const lInk = lightness(ink);
  if (lPage === null || lInk === null) return 0;
  if (Math.abs(lInk - lPage) < step) return 0;
  let lo = 0;
  let hi = 1;
  for (let i = 0; i < 24; i++) {
    const mid = (lo + hi) / 2;
    const l = lightness(mix(page, ink, mid)) ?? lPage;
    if (Math.abs(l - lPage) >= step) hi = mid;
    else lo = mid;
  }
  return Math.min(max, Math.max(min, hi));
}

/** Below this L* gap the accent is too close to the page to draw with. */
export const ACCENT_MIN_GAP = 18;

/**
 * The colour to draw in: the accent, unless it sits too close to the page
 * (an orange accent on a mid-orange page), when the text colour takes over.
 */
export function effectInk(page: string, accent: string, text: string): string {
  const lPage = lightness(page);
  const lAccent = lightness(accent);
  if (lPage !== null && lAccent !== null && Math.abs(lAccent - lPage) >= ACCENT_MIN_GAP) return accent;
  return parseColour(text) ? text : accent;
}

export type DrawnEffect = Exclude<BackgroundEffect, 'none' | 'accent-glow' | 'logo-watermark'>;

/** Per effect: the L* step a full-strength mark lands, the opacity clamps, and which colour it draws in. */
export const EFFECT_TUNING: Record<DrawnEffect, { step: number; min: number; max: number; ink: 'accent' | 'text' }> = {
  lanes: { step: 24, min: 0.14, max: 0.62, ink: 'accent' },
  topo: { step: 22, min: 0.12, max: 0.6, ink: 'accent' },
  sprint: { step: 40, min: 0.24, max: 0.88, ink: 'accent' },
  // Chalk is the page's own text colour (white chalk on dark, graphite on light), as a whiteboard would be.
  chalk: { step: 26, min: 0.1, max: 0.55, ink: 'text' },
  outline: { step: 30, min: 0.18, max: 0.72, ink: 'accent' },
  halftone: { step: 20, min: 0.12, max: 0.55, ink: 'accent' },
};

/** Below this L* a page counts as near-black, and every drawn effect steps up by the bonus. */
export const DARK_PAGE_L = 22;
export const DARK_PAGE_BONUS = 6;

export interface EffectStrength {
  ink: string;
  /** The layer's opacity; 0 means draw nothing. */
  opacity: number;
}

/**
 * The ink and layer opacity for one effect on one page. `boost` lifts a
 * small preview tile so it shows what it is (capped below text strength).
 */
export function effectStrength(effect: DrawnEffect, page: string, accent: string, text: string, boost = 1): EffectStrength {
  const t = EFFECT_TUNING[effect];
  const ink = t.ink === 'text' ? (parseColour(text) ? text : accent) : effectInk(page, accent, text);
  // Thin lines lose their colour against near-black (yellow at 15% is olive),
  // so a very dark page asks for a slightly bigger step to read the same.
  const step = t.step + ((lightness(page) ?? 50) < DARK_PAGE_L ? DARK_PAGE_BONUS : 0);
  const base = solveOpacity(page, ink, step, t.min, t.max);
  return { ink, opacity: base === 0 ? 0 : Math.min(0.9, base * boost) };
}
